import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Bell,
  ChevronDown,
  User,
  LogOut,
  Settings,
  Menu,
  ExternalLink,
  ShoppingBag,
  Star,
  X,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { Toggle } from '../ui';
import { cn } from '../../../utils/cn';
import OutletSelector from '../OutletSelector';

const Header = ({
  title,
  subtitle,
  breadcrumbs = [],
  onMenuClick,
  showSearch = true,
  actions,
}) => {
  const { currentUser, logout } = useAuth();
  const { settings, saveSettings } = useSettings();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);

  // Mock notifications - replace with real data
  const notifications = [
    { id: 1, type: 'order', title: 'New order received', time: '2 minutes ago', unread: true },
    { id: 2, type: 'review', title: 'New 5-star review', time: '1 hour ago', unread: true },
    { id: 3, type: 'order', title: 'Order completed', time: '3 hours ago', unread: false },
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleShopStatusToggle = async (isOnline) => {
    await saveSettings({ ...settings, shopOnline: isOnline });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={cn(
        'sticky top-0 z-sticky',
        'flex items-center justify-between gap-4',
        'h-20 px-6',
        'bg-gradient-to-r from-bg-primary via-bg-primary to-bg-secondary',
        'border-b border-border-light',
        'backdrop-blur-md'
      )}
    >
      {/* Left Section */}
      <div className="flex items-center gap-4 flex-1">
        {/* Mobile menu button */}
        <motion.button
          className={cn(
            'md:hidden flex items-center justify-center',
            'w-10 h-10 rounded-lg',
            'text-text-secondary hover:text-text-primary',
            'hover:bg-bg-tertiary',
            'transition-colors duration-200'
          )}
          onClick={onMenuClick}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Menu size={24} />
        </motion.button>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <nav className="hidden md:flex items-center gap-2">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id || index}>
                {index > 0 && (
                  <span className="text-text-tertiary">/</span>
                )}
                <motion.span
                  className={cn(
                    'text-sm cursor-pointer transition-colors duration-200',
                    index === breadcrumbs.length - 1
                      ? 'text-text-primary font-semibold'
                      : 'text-text-tertiary hover:text-secondary'
                  )}
                  onClick={crumb.onClick}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {crumb.label}
                </motion.span>
              </React.Fragment>
            ))}
          </nav>
        )}

        {/* Title section */}
        {title && (
          <div className="flex-1 min-w-0">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xl font-bold text-text-primary truncate"
            >
              {title}
            </motion.h1>
            {subtitle && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-sm text-text-tertiary truncate"
              >
                {subtitle}
              </motion.p>
            )}
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        {/* Outlet Selector */}
        <OutletSelector />

        {/* Search */}
        {showSearch && (
          <motion.div
            initial={{ width: 200 }}
            animate={{ width: searchFocused ? 300 : 200 }}
            className="hidden md:block relative"
          >
            <div className="relative">
              <Search
                size={18}
                className={cn(
                  'absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200',
                  searchFocused ? 'text-secondary' : 'text-text-tertiary'
                )}
              />
              <input
                type="text"
                placeholder="Search orders, customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className={cn(
                  'w-full pl-10 pr-4 py-2 rounded-lg',
                  'bg-bg-tertiary border border-border-light',
                  'text-sm text-text-primary placeholder:text-text-tertiary',
                  'transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-secondary/50 focus:border-secondary',
                  searchFocused && 'shadow-glow'
                )}
              />
              {searchQuery && (
                <motion.button
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
                >
                  <X size={16} />
                </motion.button>
              )}
            </div>
          </motion.div>
        )}

        {/* Shop Status Toggle */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-tertiary border border-border-light"
        >
          <Toggle
            checked={settings?.shopOnline || false}
            onChange={handleShopStatusToggle}
            size="sm"
          />
          <span className={cn(
            'text-sm font-medium transition-colors duration-200',
            settings?.shopOnline
              ? 'text-success'
              : 'text-error'
          )}>
            {settings?.shopOnline ? (
              <span className="flex items-center gap-1">
                <Zap size={14} className="animate-pulse" />
                Online
              </span>
            ) : (
              'Offline'
            )}
          </span>
        </motion.div>

        {/* View Website */}
        <motion.a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'hidden md:flex items-center gap-2 px-3 py-2 rounded-lg',
            'bg-bg-tertiary border border-border-light',
            'text-sm text-text-secondary hover:text-text-primary',
            'transition-all duration-200',
            'hover:border-secondary hover:shadow-glow'
          )}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ExternalLink size={16} />
          <span>View Site</span>
        </motion.a>

        {/* Notifications */}
        <div className="relative" ref={notificationsRef}>
          <motion.button
            className={cn(
              'relative flex items-center justify-center',
              'w-10 h-10 rounded-lg',
              'bg-bg-tertiary border border-border-light',
              'text-text-secondary hover:text-text-primary',
              'transition-all duration-200',
              showNotifications && 'bg-bg-elevated border-secondary shadow-glow'
            )}
            onClick={() => setShowNotifications(!showNotifications)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-error to-error-dark rounded-full text-white text-xs font-bold flex items-center justify-center"
              >
                {unreadCount}
              </motion.span>
            )}
          </motion.button>

          {/* Notifications Dropdown */}
          <AnimatePresence>
            {showNotifications && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className={cn(
                  'absolute right-0 top-12 w-80',
                  'bg-bg-secondary border border-border-light rounded-lg shadow-2xl',
                  'overflow-hidden z-dropdown'
                )}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border-light bg-gradient-to-r from-bg-primary to-bg-secondary">
                  <h4 className="text-sm font-semibold text-text-primary">Notifications</h4>
                  <button className="text-xs text-secondary hover:text-secondary-light transition-colors">
                    Mark all read
                  </button>
                </div>

                {/* Notifications List */}
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map((notification, index) => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={cn(
                        'flex items-start gap-3 p-4 hover:bg-bg-tertiary transition-colors cursor-pointer',
                        notification.unread && 'bg-bg-tertiary/50',
                        index !== notifications.length - 1 && 'border-b border-border-light'
                      )}
                    >
                      <div className={cn(
                        'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                        notification.type === 'order' ? 'bg-secondary/20 text-secondary' : 'bg-accent/20 text-accent'
                      )}>
                        {notification.type === 'order' ? <ShoppingBag size={20} /> : <Star size={20} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {notification.title}
                        </p>
                        <p className="text-xs text-text-tertiary">
                          {notification.time}
                        </p>
                      </div>
                      {notification.unread && (
                        <span className="flex-shrink-0 w-2 h-2 bg-secondary rounded-full" />
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-border-light bg-gradient-to-r from-bg-primary to-bg-secondary">
                  <button className="w-full text-sm text-secondary hover:text-secondary-light transition-colors font-medium">
                    View all notifications
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Custom actions */}
        {actions}

        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <motion.button
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'bg-bg-tertiary border border-border-light',
              'text-text-secondary hover:text-text-primary',
              'transition-all duration-200',
              showUserMenu && 'bg-bg-elevated border-secondary shadow-glow'
            )}
            onClick={() => setShowUserMenu(!showUserMenu)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-secondary to-accent flex items-center justify-center">
              <User size={18} className="text-white" />
            </div>
            <span className="hidden md:block text-sm font-medium text-text-primary">
              {currentUser?.email?.split('@')[0] || 'Admin'}
            </span>
            <motion.div
              animate={{ rotate: showUserMenu ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={16} />
            </motion.div>
          </motion.button>

          {/* User Dropdown */}
          <AnimatePresence>
            {showUserMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className={cn(
                  'absolute right-0 top-12 w-64',
                  'bg-bg-secondary border border-border-light rounded-lg shadow-2xl',
                  'overflow-hidden z-dropdown'
                )}
              >
                {/* Header */}
                <div className="p-4 border-b border-border-light bg-gradient-to-r from-bg-primary to-bg-secondary">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {currentUser?.email}
                  </p>
                  <p className="text-xs text-text-tertiary">Administrator</p>
                </div>

                {/* Menu Items */}
                <div className="p-2">
                  <motion.button
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-all"
                    whileHover={{ x: 4 }}
                  >
                    <User size={18} />
                    <span>Profile</span>
                  </motion.button>
                  <motion.button
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-all"
                    whileHover={{ x: 4 }}
                  >
                    <Settings size={18} />
                    <span>Settings</span>
                  </motion.button>
                </div>

                {/* Footer */}
                <div className="p-2 border-t border-border-light">
                  <motion.button
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-error hover:bg-error/10 transition-all"
                    onClick={handleLogout}
                    whileHover={{ x: 4 }}
                  >
                    <LogOut size={18} />
                    <span>Logout</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
